/* eslint-disable complexity */
import React, { useState } from "react";
import { Box, LinearProgress } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { FormInputText } from "@imminently/react/ui/forms";

import ExistingPatientSearchOverlay from "./ExistingPatientSearchOverlay";
import { Patient } from "@/resources/types";
import { encounterService } from "@/services";
import { encounterToFormValues } from "./genericAddPatientModal";
import { applyExistingPatientToGenericForm, patientToExternalRegisterSummary } from "./patientFormValues";
import type { PatientStatusEncounterSummary } from "./patientStatusWidgetHelpers";
import { usePatientStatusWidgetRuntime } from "./patientStatusWidgetContext";

interface PatientNameFieldProps {
  name: "firstName" | "lastName";
  label: string;
  required?: boolean;
  disabled?: boolean;
  sx?: any;
}

type ActivePatientNameOverlayField = "firstName" | "lastName" | null;
type PatientWithRecentEncounters = Patient & {
  recentEncounters?: PatientStatusEncounterSummary[];
};

// Shared reactive state to manage a single overlay instance across both name fields.
const globalOverlayState = {
  activeField: null as ActivePatientNameOverlayField,
  listeners: new Set<() => void>(),
  emit: () => {
    globalOverlayState.listeners.forEach(listener => listener());
  },
  subscribe: (listener: () => void) => {
    globalOverlayState.listeners.add(listener);
    return () => {
      globalOverlayState.listeners.delete(listener);
    };
  },
  getSnapshot: () => globalOverlayState.activeField,
  setActiveField: (field: ActivePatientNameOverlayField) => {
    if (globalOverlayState.activeField === field) return;
    globalOverlayState.activeField = field;
    globalOverlayState.emit();
  },
  closeOverlay: () => {
    if (globalOverlayState.activeField === null) return;
    globalOverlayState.activeField = null;
    globalOverlayState.emit();
  },
};

/** When resuming an encounter: backend wins unless "empty"; otherwise keep local (in-progress) values. */
const isEmptyValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (typeof value === "boolean" && value === false) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (
    typeof value === "object" &&
    value !== null &&
    "isValid" in value &&
    typeof (value as { isValid: () => boolean }).isValid === "function" &&
    !(value as { isValid: () => boolean }).isValid()
  ) {
    return true; // invalid moment (or moment-like)
  }
  return false;
};

const PatientNameField: React.FC<PatientNameFieldProps> = ({
  name,
  label,
  required = false,
  disabled = false,
  sx = {},
}) => {
  const { control, setValue, getValues, trigger } = useFormContext();
  const patientStatusRuntime = usePatientStatusWidgetRuntime();
  const [loadingEncounterId, setLoadingEncounterId] = useState<string | null>(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const firstName = useWatch({ control, name: "firstName" });
  const lastName = useWatch({ control, name: "lastName" });
  const isEditMode = useWatch({ control, name: "_isEditMode" });

  // Unknown patients should not trigger overlay or loading
  const submissionType = useWatch({ control, name: "submissionType" });
  const isUnknown = submissionType === "unknown";
  const isAnonymous = submissionType === "anonymous";
  const onOpenExternalRegisterDetails = patientStatusRuntime.canOpenExternalRegisterSecureDetails
    ? patientStatusRuntime.onOpenExternalRegisterDetails
    : undefined;
  const activeOverlayField = React.useSyncExternalStore(
    globalOverlayState.subscribe,
    globalOverlayState.getSnapshot,
    globalOverlayState.getSnapshot,
  );

  // Check if this field should show the overlay
  // Don't show in edit mode, when disabled, or for unknown/anonymous patients
  const shouldShowOverlay = activeOverlayField === name && !disabled && !isUnknown && !isAnonymous && !isEditMode;

  const handleLoadingChange = React.useCallback((isLoading: boolean) => {
    setIsSearchLoading(current => current === isLoading ? current : isLoading);
  }, []);

  const closePatientSearchOverlay = React.useCallback(() => {
    setIsSearchLoading(current => current === false ? current : false);
    globalOverlayState.closeOverlay();
  }, []);

  const handleSelectPatient = async (patient: Patient) => {
    closePatientSearchOverlay();
    await applyExistingPatientToGenericForm({ patient, getValues, setValue, trigger });
  };

  const handleSelectEncounter = async (patient: Patient, encounter: any) => {
    // Don't proceed if encounter ID is missing
    if (!encounter.id) {
      console.warn("Encounter ID is missing, cannot load encounter:", encounter);
      return;
    }

    setLoadingEncounterId(encounter.id);

    try {
      console.log("getting encounter", encounter);
      // Fetch the full encounter data using the correct service method
      const encounterData = await encounterService.getOne(encounter.id);

      if (encounterData) {
        // Close the overlay first
        closePatientSearchOverlay();

        // Use the encounterToFormValues helper to properly map the data
        const formData = encounterToFormValues(encounterData);
        const localValues = getValues();

        // Merge: prefer backend when non-empty; if backend is empty, keep local triage/in-progress values
        Object.keys(formData).forEach((key) => {
          const backendValue = (formData as Record<string, unknown>)[key];
          const localValue = (localValues as Record<string, unknown>)[key];

          if (!isEmptyValue(backendValue) || isEmptyValue(localValue)) {
            setValue(key, backendValue as never);
          }
        });

        const originalStatus = encounterData.status || "";
        const isExpected = originalStatus === "expected";

        // Set the encounter ID for update operations
        // Use shouldValidate: false to batch updates, then trigger validation once
        setValue("_encounterId", encounter.id, { shouldValidate: false });
        // Set _existingPatientId when resuming an encounter and trigger validation so the dynamic resolver uses createEditSchema (which excludes the patient's own email from duplicate checks) instead of createSchema
        setValue("_existingPatientId", patient.id, { shouldValidate: false });
        setValue("_externalRegisterSummary", patientToExternalRegisterSummary(patient), { shouldValidate: false });
        const recentEncounters = (patient as PatientWithRecentEncounters).recentEncounters;
        setValue(
          "_patientStatusRecentEncounters",
          Array.isArray(recentEncounters) ? recentEncounters : undefined,
          { shouldValidate: false },
        );
        setValue("_isEditMode", true, { shouldValidate: false });
        setValue("_encounterOriginalStatus", originalStatus, { shouldValidate: false });
        setValue("_resumeExpectedEncounter", isExpected, { shouldValidate: false });

        // Trigger full form validation after all values are set
        // This ensures the resolver sees the updated _existingPatientId and _isEditMode
        await trigger();
      }
    } catch (error) {
      console.error("Error loading encounter:", error);
    } finally {
      setLoadingEncounterId(null);
    }
  };

  const handleCloseOverlay = () => {
    closePatientSearchOverlay();
  };

  const handleFocus = () => {
    if (disabled || isUnknown || isAnonymous || isEditMode) return;
    // Set active field when focusing - overlay will show automatically if conditions are met
    globalOverlayState.setActiveField(name);
  };

  const handleKeyUp = () => {
    if (disabled || isUnknown || isAnonymous || isEditMode) return;
    // Set active field on keystroke - overlay will show automatically if conditions are met
    globalOverlayState.setActiveField(name);
  };

  // const handleBlur = () => {
  //   // Close overlay when field loses focus
  //   if (globalOverlayState.isOpen && globalOverlayState.activeField === name) {
  //     // Small delay to allow clicking on overlay items
  //     setTimeout(() => {
  //       globalOverlayState.closeOverlay();
  //       setLocalOverlayOpen(false);
  //     }, 200);
  //   }
  // };

  // If field becomes disabled, patient is unknown, or in edit mode, ensure any loading/overlay is cleared
  React.useEffect(() => {
    if (disabled || isUnknown || isAnonymous || isEditMode) {
      setIsSearchLoading(current => current === false ? current : false);
      if (activeOverlayField === name) {
        globalOverlayState.closeOverlay();
      }
    }
  }, [activeOverlayField, disabled, isUnknown, isAnonymous, isEditMode, name]);

  React.useEffect(() => {
    if (activeOverlayField !== name) {
      setIsSearchLoading(current => current === false ? current : false);
    }
  }, [activeOverlayField, name]);

  return (
    <Box sx={{ position: "relative", ...sx }}>
      <FormInputText
        name={name}
        label={label}
        required={required}
        disabled={disabled}
        control={control}
        sx={{ width: "100%" }}
        onFocus={handleFocus}
        onKeyUp={handleKeyUp}
        autoComplete="off"
        slotProps={{ htmlInput: { autoComplete: "off" } }}
        // onBlur={handleBlur}
      />

      {/* Show loading indicator below the field when searching */}
      {isSearchLoading && !disabled && !isUnknown && !isAnonymous && (
        <LinearProgress
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            borderRadius: "0 0 4px 4px"
          }}
        />
      )}

      {shouldShowOverlay && firstName && lastName && (
        <ExistingPatientSearchOverlay
          firstName={firstName}
          lastName={lastName}
          onSelectPatient={handleSelectPatient}
          onSelectEncounter={handleSelectEncounter}
          onClose={handleCloseOverlay}
          loadingEncounterId={loadingEncounterId}
          onLoadingChange={handleLoadingChange}
          onOpenExternalRegisterDetails={onOpenExternalRegisterDetails}
        />
      )}
    </Box>
  );
};

export default PatientNameField;
